import { Component, forwardRef, Input, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ControlValueAccessor,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { MultiSelectModule } from 'primeng/multiselect';
import { AccordionModule } from 'primeng/accordion';
import { InputSwitchModule } from 'primeng/inputswitch';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { ToastrService } from 'ngx-toastr';
import { showNotification } from 'src/app/helpers/show-toast';

// Interfaces for type safety
export interface MedicineMetadata {
  id: number;
  name: string;
  genericName?: string;
}

export interface Titration {
  dosage: number;
  frequency: number;
  frequencyDuration: string;
  period: number;
  periodDuration: string;
  direction: string;
}

export interface MedicationFormValue {
  isChronicMedication?: boolean;
  startDate?: Date;
  endDate?: Date;
  titrations: Titration[];
  medicineMetadata: MedicineMetadata;
  isRequired?: boolean;
}

export interface TopUsedMedication extends MedicineMetadata {
  topUsedDosage?: Titration;
}

export interface MedicationSearchResult {
  label: string;
  value: MedicineMetadata;
}

@Component({
  standalone: true,
  selector: 'medication-dosage-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MultiSelectModule,
    AccordionModule,
    InputSwitchModule,
    SelectButtonModule,
    DropdownModule,
    CalendarModule
  ],
  templateUrl: './medication-dosage-form.component.html',
  styleUrls: ['./medication-dosage-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MedicationDosageFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MedicationDosageFormComponent),
      multi: true
    }
  ]
})
export class MedicationDosageFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  // Configuration inputs
  @Input() isConcomitantTreatment: boolean = false;
  @Input() topUsedMedications: TopUsedMedication[] = [];
  @Input() requireAtLeastOne: boolean = true;
  @Input() maxMedicines?: number;
  @Input() simplifiedMode: boolean = false;
  @Input() removeButtonLabel: string = 'Remove Medicine';

  // Callback functions for external dependencies
  @Input() searchMedicationsFn?: (searchTerm: string) => Observable<MedicationSearchResult[]>;

  get isReadOnly(): boolean {
    return this.internalForm?.disabled ?? false;
  }

  // Form controls
  internalForm: FormGroup;
  private optionsSubject = new BehaviorSubject<MedicationSearchResult[]>([]);
  options$ = this.optionsSubject.asObservable();
  selectedMedicines: MedicineMetadata[] = [];

  // Date constraints
  today: Date = new Date();
  minSelectableStartDate: Date | null = null;
  maxSelectableEndDate: Date = new Date(this.today.getTime() - 2 * 24 * 60 * 60 * 1000);
  yesterday: Date = new Date(Date.now() - 86400000);

  // Accordion state
  activeIndex: number | null = null;
  lastAddedMedicineId: number | null = null;

  // Search handling
  private filterTerms = new Subject<string>();
  private destroy$ = new Subject<void>();

  // ControlValueAccessor callbacks
  private onChange: (value: MedicationFormValue[]) => void = () => { };
  private onTouched: () => void = () => { };

  // Dropdown options
  intervalOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
  ];

  longIntervalOptions = Array.from({ length: 15 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1
  }));

  directionOptions = [
    { label: 'none', value: 'لا يوجد' },
    { label: 'Before Meal', value: 'قبل الأكل' },
    { label: 'After Meal', value: 'بعد الأكل' },
    { label: 'With Food', value: 'أثناء الأكل' },
    { label: '2 hours after eating', value: 'بعد الأكل ب ساعتين' },
    { label: '15 minutes before eating', value: 'قبل الأكل ب ربع ساعه' },
    { label: '1 hour before eating', value: 'قبل الأكل ب ساعه' },
    { label: 'before sleeping', value: 'قبل النوم' },
    { label: 'on an empty stomach', value: 'على معده فارغة' }
  ];

  intervalUnitOptions = [
    { label: 'Day', value: 'يوم' },
    { label: 'Week', value: 'إسبوع' },
    { label: 'Month', value: 'شهر' }
  ];

  constructor(
    private fb: FormBuilder,
    private renderer: Renderer2,
    private toastr: ToastrService,
  ) {
    this.internalForm = this.fb.group({
      medicineForms: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.setupSearchHandler();
    this.setupFormValueChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: MedicationFormValue[]): void {
    if (value && value.length > 0) {
      this.loadExistingData(value);
    } else {
      this.clearAllMedicines();
    }
  }

  registerOnChange(fn: (value: MedicationFormValue[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.internalForm) return;

    if (isDisabled) {
      this.internalForm.disable({ emitEvent: false });
    } else {
      this.internalForm.enable({ emitEvent: false });
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.isReadOnly) {
      return null;
    }

    const errors: ValidationErrors = {};

    if (this.requireAtLeastOne && this.medicineForms.length === 0) {
      errors['noMedicinesSelected'] = true;
    }

    // Validate each medicine form
    this.medicineForms.controls.forEach((medicineForm, index) => {
      const formGroup = medicineForm as FormGroup;

      // Validate dates for concomitant treatment
      if (this.isConcomitantTreatment) {
        const startDate = formGroup.get('startDate')?.value;
        const endDate = formGroup.get('endDate')?.value;

        if (!startDate || !endDate) {
          errors[`medicine_${index}_missingDates`] = true;
        }
      }

      // Validate titrations
      const titrations = formGroup.get('titrations') as FormArray;
      if (titrations.length === 0) {
        errors[`medicine_${index}_noTitrations`] = true;
      }
    });

    return Object.keys(errors).length > 0 ? errors : null;
  }

  // Setup methods
  private setupSearchHandler(): void {
    if (!this.searchMedicationsFn) {
      return;
    }

    this.filterTerms.pipe(
      filter((term) => term.length >= 2 || term.length === 0),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || !this.searchMedicationsFn) {
          return of([]);
        }
        return this.searchMedicationsFn(term);
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => this.optionsSubject.next(results));
  }

  private setupFormValueChanges(): void {
    this.internalForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onChange(this.getValue());
        this.onTouched();
      });
  }

  // Data loading
  private loadExistingData(medications: MedicationFormValue[]): void {
    const existingIds = this.medicineForms.controls.map(
      control => control.get('medicineMetadata')?.value.id
    );

    this.clearAllMedicines();

    // Track new medicines as we load
    let lastNewMedicineId: number | null = null;

    medications.forEach(med => {
      const medicineForm = this.createMedicineFormGroup(med.medicineMetadata, med.isRequired ?? false);

      if (!this.isConcomitantTreatment) {
        medicineForm.patchValue({ isChronicMedication: med.isChronicMedication });
      }

      if (this.isConcomitantTreatment && med.startDate && med.endDate) {
        medicineForm.patchValue({
          startDate: new Date(med.startDate),
          endDate: new Date(med.endDate)
        });
      }

      this.medicineForms.push(medicineForm);
      this.selectedMedicines.push(med.medicineMetadata);

      // Track if this is a NEW medicine (wasn't in the form before)
      if (!existingIds.includes(med.medicineMetadata.id)) {
        lastNewMedicineId = med.medicineMetadata.id;
      }

      const titrationsArray = medicineForm.get('titrations') as FormArray;
      if (med.titrations && med.titrations.length > 0) {
        med.titrations.forEach(titration => {
          titrationsArray.push(this.createTitrationFormGroup(titration));
        });
      }
    });

    // If there were new medicines added, open the last new one
    // Otherwise, open the previously tracked lastAddedMedicineId
    const idToOpen = lastNewMedicineId !== null ? lastNewMedicineId : this.lastAddedMedicineId;

    if (idToOpen !== null) {
      const index = this.medicineForms.controls.findIndex(
        control => control.get('medicineMetadata')?.value.id === idToOpen
      );
      this.activeIndex = index !== -1 ? index : null;
      if (lastNewMedicineId !== null) {
        this.lastAddedMedicineId = lastNewMedicineId;
      }
    } else {
      this.activeIndex = null;
    }
  }

  private clearAllMedicines(): void {
    while (this.medicineForms.length) {
      this.medicineForms.removeAt(0);
    }
    this.selectedMedicines = [];
  }

  // Form getters
  get medicineForms(): FormArray {
    return this.internalForm.get('medicineForms') as FormArray;
  }

  getTitrations(index: number): FormArray {
    return this.medicineForms.at(index).get('titrations') as FormArray;
  }

  // Form value extraction
  getValue(): MedicationFormValue[] {
    return this.medicineForms.controls.map(control => {
      const formGroup = control as FormGroup;
      return formGroup.getRawValue();
    });
  }

  // Form group creation
  private createMedicineFormGroup(medicine: MedicineMetadata, isRequired: boolean = false): FormGroup {
    const baseControls: any = {
      isChronicMedication: [!this.isConcomitantTreatment && false],
      titrations: this.fb.array([]),
      medicineMetadata: [medicine],
      isRequired: [isRequired]
    };

    if (this.isConcomitantTreatment) {
      baseControls['startDate'] = [null, Validators.required];
      baseControls['endDate'] = [null, Validators.required];
    }

    return this.fb.group(baseControls);
  }

  private createTitrationFormGroup(titration?: Partial<Titration>): FormGroup {
    return this.fb.group({
      dosage: [titration?.dosage ?? 1, Validators.required],
      frequency: [titration?.frequency ?? 1, Validators.required],
      frequencyDuration: [titration?.frequencyDuration ?? 'يوم', Validators.required],
      period: [titration?.period ?? 1, Validators.required],
      periodDuration: [titration?.periodDuration ?? 'يوم', Validators.required],
      direction: [titration?.direction ?? 'لا يوجد', Validators.required],
    });
  }

  // Medicine management
  addMedicine(medicine: MedicineMetadata, isRequired: boolean = false): void {
    const medicineForm = this.createMedicineFormGroup(medicine, isRequired);
    this.medicineForms.push(medicineForm);
    this.addTitration(this.medicineForms.length - 1);
    this.lastAddedMedicineId = medicine.id;
    this.activeIndex = this.medicineForms.length - 1;
  }

  removeMedicine(id: number): void {
    const index = this.medicineForms.controls.findIndex(
      control => control.get('medicineMetadata')?.value.id === id
    );

    if (index === -1) return;

    // Prevent removing required medicines
    const isRequired = this.medicineForms.at(index).get('isRequired')?.value;
    if (isRequired) {
      console.warn('Cannot remove required medicine');
      return;
    }

    this.getTitrations(index).clear();
    this.medicineForms.removeAt(index);
    this.selectedMedicines = this.selectedMedicines.filter(med => med.id !== id);
  }

  // Titration management
  addTitration(medicineIndex: number): void {
    this.getTitrations(medicineIndex).push(this.createTitrationFormGroup());
  }

  removeTitration(medicineIndex: number, titrationIndex: number): void {
    this.getTitrations(medicineIndex).removeAt(titrationIndex);
  }

  // Medicine selection handling
  onMedicineSelect(event: any): void {
    const newlySelectedMedicines: MedicineMetadata[] = event.value;

    // Find medicines to add
    const medicinesToAdd = newlySelectedMedicines.filter(medicine =>
      !this.medicineForms.controls.some(control =>
        control.get('medicineMetadata')?.value.id === medicine.id
      )
    );

    this.enforceMaxMedicines(medicinesToAdd.length);

    // Add new medicines
    medicinesToAdd.forEach(medicine => this.addMedicine(medicine));

    // Sync selection with forms
    this.syncSelectedMedicinesWithForms();

    // Close dropdown
    const closeButton = document.querySelector('.p-multiselect-close.p-link') as HTMLButtonElement;
    if (closeButton) {
      closeButton.click();
    }
  }

  quickAddMedication(medication: TopUsedMedication): void {
    if (this.isReadOnly) return;

    // Toggle if already selected
    if (this.isMedicationSelected(medication.id)) {
      this.removeMedicine(medication.id);
      return;
    }

    this.enforceMaxMedicines(1);

    // Add medication
    this.addMedicine(medication);

    // Apply predefined dosage if available
    if (medication.topUsedDosage) {
      const medicineIndex = this.medicineForms.length - 1;
      const titrations = this.getTitrations(medicineIndex);

      if (titrations.length > 0) {
        const firstTitration = titrations.at(0) as FormGroup;
        firstTitration.patchValue(medication.topUsedDosage);
      }
    }

    this.syncSelectedMedicinesWithForms();
  }

  isMedicationSelected(medicationId: number): boolean {
    return this.selectedMedicines.some(med => med.id === medicationId);
  }

  private syncSelectedMedicinesWithForms(): void {
    this.selectedMedicines = this.medicineForms.controls.map(
      control => control.get('medicineMetadata')?.value
    );
  }

  // Search handling
  onFilter(event: { filter: string }): void {
    if (!event.filter.trim()) return;
    this.filterTerms.next(event.filter);
  }

  focusSearchBar(): void {
    const filterInput = this.renderer.selectRootElement('.p-multiselect-filter', true);
    if (filterInput) {
      filterInput.focus();
    }
  }

  resetPanel(): void {
    this.optionsSubject.next([]);
    this.filterTerms.next('');
  }

  // Date handling
  updateMinEndDate(index: number): void {
    const formGroup = this.medicineForms.at(index) as FormGroup;
    const startDate = formGroup.get('startDate')?.value;
    if (startDate) {
      this.minSelectableStartDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
    }
  }

  updateMaxStartDate(index: number): void {
    const formGroup = this.medicineForms.at(index) as FormGroup;
    const endDate = formGroup.get('endDate')?.value;
    if (endDate) {
      this.maxSelectableEndDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
    }
  }

  // Accordion handling
  onAccordionOpen(event: any): void {
    this.activeIndex = event.index;
  }

  onAccordionClose(event: any): void {
    if (this.activeIndex === event.index) {
      this.activeIndex = null;
    }
  }

  // Utility methods
  trackById(index: number, medicineForm: AbstractControl): number {
    return medicineForm.get('medicineMetadata')?.value.id;
  }

  getOrdinalSuffix(index: number): string {
    const suffixes = ["th", "st", "nd", "rd"];
    const v = index % 100;
    return index + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  }

  private enforceMaxMedicines(medicinesToAdd: number): void {
    if (!this.maxMedicines) return;

    const currentCount = this.medicineForms.length;
    const excessCount = currentCount + medicinesToAdd - this.maxMedicines;

    if (excessCount > 0) {
      // Remove oldest medicines
      for (let i = 0; i < excessCount; i++) {
        const oldestMedicine = this.medicineForms.at(0);
        const oldestId = oldestMedicine.get('medicineMetadata')?.value.id;
        if (oldestId) {
          this.removeMedicine(oldestId);
        }
      }

      showNotification(
        'warning',
        `Only ${this.maxMedicines} medication${this.maxMedicines > 1 ? 's' : ''} can be selected at a time. Previous selection${excessCount > 1 ? 's' : ' '} removed.`,
        this.toastr
      );
    }
  }
}